<script setup lang="ts">

import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import checkIcon from "../../public/icons/check.svg";
import uncheckIcon from "../../public/icons/uncheck.svg";

const model = defineModel<string | number | null>()

const props = defineProps<{
    id: string,
    label?: string,
    disabled?: boolean,
    required?: boolean,
    error?: string,
    options: { value: string | number, label: string }[],
}>()

const emit = defineEmits(['clearError'])

const open = ref(false)
const container = ref<HTMLElement | null>(null)

const selectedLabel = computed(() =>
    props.options.find(o => o.value === model.value)?.label ?? null
)

const select = (value: string | number) => {
    model.value = value
    open.value = false
}

const handleClickOutside = (e: MouseEvent) => {
    if (container.value && !container.value.contains(e.target as Node)) {
        open.value = false
    }
}

watch(model, () => emit('clearError'))

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))

</script>

<template>
    <div class="space-y-4 relative" ref="container">
        <label :for="id">
            {{ label }}<span v-if="required" class="text-primary ml-2">*</span>
        </label>

        <div
            class="relative flex items-center border border-solid rounded duration-150 cursor-pointer px-4 h-30"
            :class="[
                error ? 'border-primary' : 'border-gray-light',
                disabled ? 'bg-gray-accent pointer-events-none' : 'bg-white hover:bg-gray-accent'
            ]"
            @click="open = !open"
        >
            <span class="text-sm grow" :class="selectedLabel ? 'text-dark' : 'text-gray-400'">
                {{ selectedLabel ?? null }}
            </span>
            <img src="../../public/icons/arrow-down.svg" class="w-4 shrink-0 !m-0 !p-0 transition-transform duration-150" :class="open ? 'rotate-180' : ''" alt="">
        </div>

        <div v-if="open" class="absolute z-50 w-full bg-white border border-solid border-gray-light rounded shadow-md mt-1 max-h-[200px] overflow-y-auto">
            <div
                v-for="option in options"
                :key="option.value"
                class="flex items-center gap-4 px-4 py-2 text-xs cursor-pointer hover:bg-gray-accent"
                :class="option.value === model ? 'text-dark font-medium' : 'text-gray'"
                @click="select(option.value)"
            >
                <img
                    :src="option.value === model ? checkIcon : uncheckIcon"
                    class="w-6 shrink-0 !m-0 !p-0"
                    alt=""
                />
                {{ option.label }}
            </div>
        </div>

        <div v-if="error" class="text-xs text-primary mt-2">{{ error }}</div>
    </div>
</template>
